import { Injectable, OnDestroy } from '@angular/core';
import { Location } from 'src/app/modules/route-planner/interfaces/route-planning/location.interface';
import { ModalService } from '../modal/modal.service';
import {
  Observable,
  ReplaySubject,
  defer,
  from,
  take,
  map,
  catchError,
  of,
  BehaviorSubject,
  switchMap,
} from 'rxjs';
import { LocalStorageService } from '../local-storage/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class UserLocationService implements OnDestroy {
  private _watchId: number | null = null;
  private _userLocation: Location | null = null;
  // Track permission state to avoid frequent browser API calls
  private _permissionState: PermissionState | null = null;
  // Flag to track if a permission popup is currently displayed
  private _isPopupDisplayed: boolean = false;
  // Store the permission status result to listen for changes
  private _permissionStatusResult: PermissionStatus | null = null;
  // Store the permission change handler to properly remove it later
  private _permissionChangeHandler: (() => void) | null = null;
  // Track if first position update has happened
  private _firstPositionReceived = false;
  // Store subscription for cleanup
  private _permissionFallbackSubscription: any = null;

  // Observable for permission state changes
  private _permissionStateChanged = new BehaviorSubject<boolean>(false);
  public $permissionStateChanged = this._permissionStateChanged.asObservable();

  $userLocationChanged = new ReplaySubject<{
    firstLocationCheck?: boolean;
    location: Location;
  }>(1);

  constructor(
    private readonly modalService: ModalService,
    private readonly localStorageService: LocalStorageService
  ) {
    // Initialize permission state by checking once on service creation
    this.updatePermissionState();
  }

  private positionToLocation(position: GeolocationPosition): Location {
    const c = position.coords;
    const loc: Location = {
      lat: c.latitude,
      lng: c.longitude,
    };
    if (c.accuracy != null && !Number.isNaN(c.accuracy)) {
      loc.accuracyMeters = c.accuracy;
    }
    return loc;
  }

  /**
   * Checks if the geolocation API is available on this device/browser
   */
  public isGeolocationAvailable(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Updates the internal permission state by checking with the browser API
   * and sets up a listener for permission changes if supported
   */
  private updatePermissionState(): void {
    if (!this.isGeolocationAvailable()) {
      this._permissionState = null;
      return;
    }

    // Remove previous listener if exists
    if (this._permissionStatusResult && this._permissionChangeHandler) {
      this._permissionStatusResult.removeEventListener('change', this._permissionChangeHandler);
      this._permissionChangeHandler = null;
    }

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((result) => {
        const oldState = this._permissionState;
        this._permissionState = result.state;
        this._permissionStatusResult = result;

        // If permission state has changed, emit event
        if (oldState !== result.state) {
          this._permissionStateChanged.next(result.state === 'granted');
        }

        // Set up permission state change listener if browser supports it
        if (result.addEventListener) {
          // Store handler reference for proper cleanup
          this._permissionChangeHandler = () => {
            this._permissionState = result.state;
            this._permissionStateChanged.next(result.state === 'granted');

            // If permission was just granted, start watching
            if (result.state === 'granted') {
              this.startWatchingLocation();
            } else if (result.state === 'denied') {
              // If permission was just denied and we're watching, stop
              if (this._watchId !== null) {
                navigator.geolocation.clearWatch(this._watchId);
                this._watchId = null;
              }
            }
          };
          result.addEventListener('change', this._permissionChangeHandler);
        }

        // If permission is already granted, start watching immediately
        if (result.state === 'granted') {
          this.startWatchingLocation();
        } else if (result.state === 'prompt') {
          // For 'prompt' state, don't automatically request permission
          // Wait for explicit user action (button click) to show permission dialog
          // This prevents unwanted permission prompts on page load
        }
      })
      .catch((err) => {
        console.warn('Permission API query failed, using fallback:', err);
        // Fallback to direct geolocation request if Permissions API fails
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this._permissionState = 'granted';
            this._permissionStateChanged.next(true);
            this.startWatchingLocation();
          },
          (error) => {
            // Only set denied if it's actually a permission error
            if (error.code === error.PERMISSION_DENIED) {
              this._permissionState = 'denied';
              this._permissionStateChanged.next(false);
            } else {
              // For other errors (timeout, unavailable), keep state as is
              // and let watchPosition handle retries
              console.warn('Geolocation fallback error (non-permission):', error.message);
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 20000,
            maximumAge: 60000,
          }
        );
      });
  }

  private set userLocation(location: Location) {
    const isFirstUpdate = !this._firstPositionReceived;
    this._firstPositionReceived = true;

    if (
      location &&
      (location.lat !== this._userLocation?.lat ||
        location.lng !== this._userLocation?.lng)
    ) {
      this.$userLocationChanged.next({
        firstLocationCheck: isFirstUpdate,
        location: location,
      });
    }
    this._userLocation = location;
  }

  public get userLocation(): Location | null {
    return this._userLocation;
  }

  /**
   * Checks if the browser currently has geolocation permissions
   * This checks the actual browser API rather than stored values
   */
  public checkPermissions(): Observable<boolean> {
    if (!this.isGeolocationAvailable()) {
      return of(false);
    }
  
    // If Permissions API not available (e.g., older Safari), skip straight to fallback
    if (!navigator.permissions || !navigator.permissions.query) {
      return this.checkPermissionsFallback();
    }
  
    return defer(() : Observable<boolean> =>
      from(navigator.permissions.query({ name: 'geolocation' as PermissionName })).pipe(
        take(1),
        switchMap((result) => {
          this._permissionState = result.state;
  
          if (result.state === 'granted') {
            return of(true);
          }
  
          if (result.state === 'denied') {
            return of(false);
          }
  
          // If 'prompt' or anything else, fallback to real request (e.g., Safari quirk)
          return this.checkPermissionsFallback();
        }),
        catchError((err) => {
          console.warn('Permission API query failed:', err);
          return this.checkPermissionsFallback();
        })
      )
    );
  }
  

  private checkPermissionsFallback(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (!navigator.geolocation) {
        console.warn('Geolocation not supported.');
        observer.next(false);
        observer.complete();
        return;
      }

      // Use high accuracy for fallback check
      navigator.geolocation.getCurrentPosition(
        () => {
          observer.next(true);
          observer.complete();
        },
        (error) => {
          console.warn('Geolocation error:', error.message, error.code);
          observer.next(false);
          observer.complete();
        },
        {
          enableHighAccuracy: true, // High accuracy is critical
          timeout: 20000, // Increased timeout for mobile devices
          maximumAge: 60000, // Accept cached position up to 60 seconds old
        }
      );
    }).pipe(
      take(1),
      catchError((err) => {
        console.error('Unexpected error during geolocation fallback:', err);
        return of(false);
      })
    );
  }
  
  

  /**
   * Synchronous property to check if location permissions are granted
   * Uses cached permission state for efficiency, may not always be up-to-date
   */
  public get hasPermissions(): boolean {
    return this._permissionState === 'granted';
  }

  /**
   * Checks if the user has explicitly denied location permission
   */
  public get permissionDenied(): boolean {
    return this._permissionState === 'denied';
  }

  /**
   * Synchronously checks if we have a current user location
   * This doesn't check permissions, only if we have location data
   */
  public get hasUserLocation(): boolean {
    return this._userLocation !== null;
  }

  /**
   * Silent permission check that doesn't show popups
   * Used for automatic checks during app initialization
   */
  public checkPermissionsSilently(): Observable<boolean> {
    return this.checkPermissions();
  }

  /**
   * One immediate geolocation read when permission is already granted (map marker, etc.).
   * Does not open permission UI. Complements watchPosition, which can lag on first fix.
   */
  public requestImmediatePositionIfPermitted(): void {
    if (!this.isGeolocationAvailable()) return;
    if (this._permissionState !== 'granted') return;
    this.getCurrentPositionImmediately();
  }

  /**
   * Requests location permission regardless of previous state
   * This is used when user explicitly requests location via button click
   * This will show the permission dialog
   */
  public requestLocationPermissionForced(): Observable<boolean> {
    // Update permission state first to ensure fresh check
    this.updatePermissionState();

    // Get current position with high accuracy immediately to show location icon on first click
    if (this.hasPermissions) {
      this.getCurrentPositionImmediately();
    }

    return this.requestLocationPermission(true);
  }

  /**
   * Get current position immediately with optimized settings for mobile
   * This helps ensure we have location data as soon as possible
   */
  private getCurrentPositionImmediately(): void {
    if (!this.isGeolocationAvailable()) return;

      // Try with high accuracy - accuracy is critical
    const options: PositionOptions = {
      enableHighAccuracy: true, // High accuracy is critical
      timeout: 20000, // Increased timeout for GPS
      maximumAge: 60000, // Accept cached position up to 60 seconds old for faster initial response
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this._permissionState = 'granted';
        this.userLocation = this.positionToLocation(position);
      },
      (error) => {
        // If high accuracy fails, retry with longer timeout
        const retryOptions: PositionOptions = {
          enableHighAccuracy: true, // Still try high accuracy
          timeout: 30000, // Even longer timeout for retry
          maximumAge: 0,
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this._permissionState = 'granted';
            this.userLocation = this.positionToLocation(position);
          },
          (retryError) => {
            // Just log the error, but don't update permission state
            // as this is just a single attempt
            console.warn('Error getting immediate position (both attempts failed):', retryError);
          },
          retryOptions
        );
      },
      options
    );
  }

  /**
   * Base method for requesting location permission
   * @param showPopup Whether to show the custom popup if permission is not granted
   */
  private requestLocationPermission(showPopup: boolean): Observable<boolean> {
    return defer(
      () =>
        new Observable<boolean>((observer) => {
          this.checkPermissions().subscribe((isGranted) => {
            if (isGranted) {
              this.startWatchingLocation();
              // Try to get a position immediately
              this.getCurrentPositionImmediately();
              observer.next(true);
            } else if (showPopup && !this._isPopupDisplayed) {
              // Only show popup if explicitly requested and not already showing one
              this._isPopupDisplayed = true;

              // Show our custom permission modal first
              this.modalService
                .openLocationUsePermissionModal()
                .pipe(take(1))
                .subscribe((consent: boolean) => {
                  if (consent) {
                    // If user consents in our modal, trigger the browser's permission prompt
                    // Use high accuracy settings for precise location
                    const options: PositionOptions = {
                      enableHighAccuracy: true, // High accuracy is critical
                      timeout: 20000, // Increased timeout for mobile devices
                      maximumAge: 60000, // Accept cached position up to 60 seconds old for faster initial response
                    };

                    navigator.geolocation.getCurrentPosition(
                      (position) => {
                        // Permission granted by browser
                        this._isPopupDisplayed = false;
                        this.updatePermissionState();
                        this.startWatchingLocation();

                        // Update location immediately
                        this.userLocation = this.positionToLocation(position);

                        observer.next(true);
                      },
                      (error) => {
                        // Permission denied by browser
                        this._isPopupDisplayed = false;
                        this.updatePermissionState();

                        // Better error messaging based on error code
                        let errorMessage: string;
                        switch (error.code) {
                          case error.PERMISSION_DENIED:
                            errorMessage =
                              'Location access was denied by the user.';
                            // Show the permission denied modal without device check
                            this.modalService.openLocationPermissionDeniedModal();
                            break;
                          case error.POSITION_UNAVAILABLE:
                            errorMessage =
                              'Location information is unavailable. Please check your device settings.';
                            break;
                          case error.TIMEOUT:
                            errorMessage =
                              'The request to get user location timed out.';
                            break;
                          default:
                            errorMessage =
                              'An unknown error occurred while trying to access location.';
                        }
                        console.error(
                          'Error getting location:',
                          errorMessage,
                          error
                        );

                        observer.next(false);
                      },
                      options
                    );
                  } else {
                    // User denied in our modal
                    this._isPopupDisplayed = false;
                    observer.next(false);
                  }
                });
            } else {
              // Don't show popup, just return current status
              observer.next(false);
            }
          });
        })
    );
  }

  /**
   * Observable that emits the current consent state
   * This version doesn't show any popups - use for automatic checks
   */
  public get consentGranted$(): Observable<boolean> {
    return this.requestLocationPermission(false);
  }

  public async requestLocationPermissions(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.checkPermissions().subscribe((isGranted) => {
        if (isGranted) {
          this.startWatchingLocation();
          resolve(true);
        } else {
          // Don't show popups here - this is only for automatic checks
          resolve(false);
        }
      });
    });
  }

  private startWatchingLocation(forceRestart: boolean = false): void {
    // Stop existing watch if force restart is requested
    if (forceRestart && this._watchId !== null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }

    if (!this._watchId) {
      // Optimize options for mobile devices with high accuracy
      // Use cached position if available (up to 60 seconds old) for faster response
      const options = {
        enableHighAccuracy: true, // High accuracy is critical for route tracking
        timeout: 20000, // Increased timeout for mobile devices (GPS can be slow)
        maximumAge: 60000, // Accept cached position up to 60 seconds old for faster initial response
      };

      this._watchId = navigator.geolocation.watchPosition(
        (position) => {
          // Update permission state when we successfully get location
          this._permissionState = 'granted';
          this.userLocation = this.positionToLocation(position);

          // After getting first position, optionally switch to high accuracy
          // This is done automatically by watchPosition if we restart with high accuracy
        },
        (error) => {
          // More detailed error handling with retry logic for certain errors
          let errorMessage: string;
          let shouldRetry = false;
          
          switch (error.code) {
            case error.PERMISSION_DENIED:
              this._permissionState = 'denied';
              errorMessage = 'Location access was denied by the user.';
              // If permission was actively denied while watching, show the message
              // Works for all device types
              this.modalService.openLocationPermissionDeniedModal();
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage =
                'Location information is unavailable. Please check your device settings.';
              // Retry with lower accuracy if high accuracy failed
              shouldRetry = true;
              break;
            case error.TIMEOUT:
              errorMessage = 'The request to get user location timed out.';
              // Retry with cached position allowed
              shouldRetry = true;
              break;
            default:
              errorMessage =
                'An unknown error occurred while trying to access location.';
          }

          console.warn('Error watching location:', errorMessage, error);

          // Retry with more lenient options if appropriate
          if (shouldRetry && this._watchId !== null) {
            // Clear current watch
            navigator.geolocation.clearWatch(this._watchId);
            this._watchId = null;

            // Retry with more lenient options after a short delay
            setTimeout(() => {
              if (!this._watchId) {
                const retryOptions = {
                  enableHighAccuracy: true, // Still try high accuracy on retry
                  timeout: 25000, // Longer timeout for retry
                  maximumAge: 300000, // Accept cached position up to 5 minutes old
                };

                this._watchId = navigator.geolocation.watchPosition(
                  (position) => {
                    this._permissionState = 'granted';
                    this.userLocation = this.positionToLocation(position);
                  },
                  (retryError) => {
                    console.warn('Retry failed for location:', retryError);
                    // Don't retry again to avoid infinite loop
                  },
                  retryOptions
                );
              }
            }, 2000); // Wait 2 seconds before retry
          }
        },
        options
      );
    }
  }

  ngOnDestroy(): void {
    if (this._watchId !== null) {
      navigator.geolocation.clearWatch(this._watchId);
      this._watchId = null;
    }

    // Unsubscribe from permission fallback if exists
    if (this._permissionFallbackSubscription) {
      this._permissionFallbackSubscription.unsubscribe();
      this._permissionFallbackSubscription = null;
    }

    // Remove event listener if it was added
    if (
      this._permissionStatusResult &&
      this._permissionChangeHandler &&
      this._permissionStatusResult.removeEventListener
    ) {
      this._permissionStatusResult.removeEventListener('change', this._permissionChangeHandler);
      this._permissionChangeHandler = null;
    }

    this.$userLocationChanged.complete(); // Complete the ReplaySubject
    this._permissionStateChanged.complete(); // Complete the BehaviorSubject
  }
}
